import { useMemo, useState } from 'react'
import chevronDoubleRightIcon from '@/assets/icons/chevron-double-right.png'
import { MaterialIcon } from '@/ui/material-icon'
import { Button } from '@/ui/button'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/ui/dropdown-menu'
import { Skeleton } from '@/ui/skeleton'
import type { RegisteredSourcePickerItem } from '@/features/adp/hooks/useRegisteredDataSources'
import styles from './ChooseDataDestinationSheet.module.scss'
import { cn } from '@/shared/lib/utils'

const DATE_RANGE_OPTIONS = [
  { value: 'all_time', label: 'All Time' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last_7_days', label: 'Last 7 Days' },
  { value: 'last_30_days', label: 'Last 30 Days' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'this_year', label: 'This Year' },
  { value: 'last_year', label: 'Last Year' },
]

const DESTINATION_CARD_SKELETON_COUNT = 5

export type ChooseDataDestinationSheetProps = {
  onClose: () => void
  configuringNodeId?: string | null
  onSelectDestination?: (destination: RegisteredSourcePickerItem, configuringNodeId: string | null) => void
  onAddNewDestination?: (configuringNodeId: string | null) => void
  allowedInstance?: string | null
  isLoading?: boolean
  loadError?: string | null
  destinations?: RegisteredSourcePickerItem[]
  selectedDateFilter?: string | null
  onDateFilterChange?: (value: string | null) => void
  searchQuery?: string
  onSearchQueryChange?: (value: string) => void
}

export function ChooseDataDestinationSheet({
  onClose,
  configuringNodeId = null,
  onSelectDestination,
  onAddNewDestination,
  allowedInstance = null,
  isLoading = false,
  loadError = null,
  destinations = [],
  selectedDateFilter = null,
  onDateFilterChange,
  searchQuery = '',
  onSearchQueryChange,
}: Readonly<ChooseDataDestinationSheetProps>) {
  const [filtersOpen, setFiltersOpen] = useState(false)

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return destinations
    return destinations.filter(
      d => d.kind.toLowerCase().includes(q) || d.connectionName.toLowerCase().includes(q)
    )
  }, [searchQuery, destinations]);

  return (
    <aside
      className={styles.root}
      aria-label="Choose a data destination"
      data-configuring-node={configuringNodeId ?? undefined}
    >
      <div className={styles.inner}>
        <div className={styles.headerRow}>
          <button type="button" className={styles.closeChevron} onClick={onClose} aria-label="Close">
            <img src={chevronDoubleRightIcon} alt="" className={styles.closeChevronIcon} aria-hidden />
          </button>
          <h2 className={styles.title}>Choose a data Destination</h2>
        </div>

        <div className={styles.divider} />
        <p className={styles.subtitle}>
          Select an existing destination or add a new one to begin configuring and validating output
          settings.
        </p>

        <div className={styles.searchRow}>
          <div className={styles.searchInput}>
            <MaterialIcon name="search" size={18} className={styles.searchGlyph} aria-hidden />
            <input
              type="search"
              placeholder="Search Destination"
              value={searchQuery}
              onChange={e => {
                const nextValue = e.target.value
                onSearchQueryChange?.(nextValue)
              }}
              aria-label="Search destinations"
            />
          </div>
          <DropdownMenu open={filtersOpen} onOpenChange={setFiltersOpen}>
            <DropdownMenuTrigger asChild>
              <button type="button" className={styles.filtersBtn}>
                <MaterialIcon name="add_circle" size={18} aria-hidden />
                Filters
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 p-3">
              <DropdownMenuLabel className="mb-2">Date Range</DropdownMenuLabel>
              <DropdownMenuSeparator className="mb-3" />
              <div className={styles.dateRangeContainer}>
                {DATE_RANGE_OPTIONS.map(option => (
                  <Button
                    key={option.value}
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      onDateFilterChange?.(option.value === 'all_time' ? null : option.value)
                      setFiltersOpen(false)
                    }}
                    className={`${styles.dateRangeOptionButton} ${(selectedDateFilter ?? 'all_time') === option.value ? styles.selected : ''}`}
                  >
                    <div className={styles.radioIndicator}>
                      <div className={styles.radioDot} />
                    </div>
                    <span>{option.label}</span>
                  </Button>
                ))}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className={styles.grid}>
          <button
            type="button"
            className={styles.addCard}
            onClick={() => onAddNewDestination?.(configuringNodeId)}
          >
            <MaterialIcon name="add" size={40} className={styles.addIcon} aria-hidden />
            <span className={styles.addLabel}>Add New Destination</span>
          </button>

          {isLoading
            ? Array.from({ length: DESTINATION_CARD_SKELETON_COUNT }, (_, index) => (
              <div
                key={`destination-skeleton-${index + 1}`}
                className={styles.sourceCardSkeleton}
                aria-hidden="true"
              >
                <Skeleton className={styles.sourceCardSkeletonIcon} />
                <div className={styles.sourceCardSkeletonText}>
                  <Skeleton className={styles.sourceCardSkeletonTitle} />
                  <Skeleton className={styles.sourceCardSkeletonSubtitle} />
                  <Skeleton className={styles.sourceCardSkeletonSubtitleShort} />
                </div>
              </div>
            ))
            : filtered.map(d => (
              <button
                key={d.id}
                type="button"
                className={styles.sourceCard}
                onClick={() => onSelectDestination?.(d, configuringNodeId)}
              >
                <div className={styles.iconWell}>
                  <img
                    src={d.image}
                    alt={d.databaseName}
                  />
                </div>
                <div className={styles.sourceText}>
                  <span className={styles.sourceKind}>{d.kind}</span>
                  <span className={styles.sourceName}>{d.connectionName}</span>
                </div>
              </button>
            ))}
        </div>
        {!isLoading && loadError && (
          <p className={cn(styles.subtitle, "!mt-2")} role="alert">
            {loadError}
          </p>
        )}
        {!isLoading && !loadError && filtered.length === 0 && (
          <p className={cn(styles.subtitle, "!mt-2")}>No registered destinations found for {allowedInstance}.</p>
        )}
      </div>
    </aside>
  )
}
